import React, { useMemo } from 'react';
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Breadcrumbs,
  Divider,
  Link,
  ThemeProvider,
  Typography,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import DevicesIcon from '@mui/icons-material/Devices';

import { useAPI } from '../Hooks/useAPIs';
import { ErrorState, LoadingState } from '../common/States/States';
import DeviceInsightsDetailPanel from './components/DeviceInsightsDetailPanel';
import WiviSyncNotice from './components/WiviSyncNotice';
import './DeviceView.css';

const DeviceDetailView = ({ activeTheme }) => {
  const { deviceId } = useParams();
  const location = useLocation();
  const { apiCall, isMsalReady } = useAPI();

  const fallbackDevice = location.state?.device || null;
  const returnTo = location.state?.returnTo || '/devices';

  const deviceQuery = useQuery({
    queryKey: ['device-detail', deviceId],
    queryFn: async () => {
      const response = await apiCall('GET', `api/v1/devices/${deviceId}/`);
      return response.data;
    },
    enabled: isMsalReady && Boolean(deviceId),
    placeholderData: fallbackDevice,
  });

  const device = deviceQuery.data || fallbackDevice;

  const breadcrumbLabel = useMemo(() => {
    if (device?.vin) {
      return device.vin;
    }
    if (device?.mac_addr) {
      return device.mac_addr;
    }
    return `Device ${deviceId}`;
  }, [device, deviceId]);

  if (deviceQuery.isLoading && !device) {
    return (
      <ThemeProvider theme={activeTheme}>
        <Box className="device-detail-page-container">
          <LoadingState message="Loading device details..." />
        </Box>
      </ThemeProvider>
    );
  }

  if (deviceQuery.isError && !device) {
    return (
      <ThemeProvider theme={activeTheme}>
        <Box className="device-detail-page-container">
          <ErrorState
            title="Unable to load device"
            message="The device details request failed."
            onRetry={() => deviceQuery.refetch()}
          />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={activeTheme}>
      <Box className="device-detail-page-container">
        <Box className="device-detail-page-content">
          <Box sx={{ mb: 4 }}>
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 1 }}>
              <Link
                component={RouterLink}
                to={returnTo}
                color="text.secondary"
                underline="hover"
                sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', fontSize: '0.875rem' }}
              >
                <DevicesIcon sx={{ mr: 0.5, fontSize: '1.1rem' }} />
                Devices
              </Link>
              <Typography color="text.primary" sx={{ fontSize: '0.875rem' }}>
                {breadcrumbLabel}
              </Typography>
            </Breadcrumbs>

            <Typography variant="h4" fontWeight={500} sx={{ mt: 2, mb: 0 }}>
              {breadcrumbLabel}
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <WiviSyncNotice />

          <DeviceInsightsDetailPanel device={device} />
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default DeviceDetailView;